/**
 * Workflow Actions — maps WORKFLOW_REGISTRY entries to QuickAction objects.
 *
 * Each workflow becomes a `kind: 'workflow-run'` quick action. The `prompt`
 * field serves as a display/fallback string (not sent to the AI when the
 * consumer dispatches via `workflowId`).
 */

import {
  Clock,
  Zap,
  Workflow as WorkflowIcon,
  Briefcase,
  BarChart3,
  HelpCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { WORKFLOW_REGISTRY } from "@flowstate/types/workflow-registry";
import type { WorkflowIconKey } from "@flowstate/types";
import type { QuickAction, QuickActionCategory } from "./types";

const ICON_MAP: Record<WorkflowIconKey, LucideIcon> = {
  clock: Clock,
  zap: Zap,
  workflow: WorkflowIcon,
  briefcase: Briefcase,
  "bar-chart": BarChart3,
};

const CATEGORY_MAP: Record<string, QuickActionCategory> = {
  automation: "creation",
  analysis: "analysis",
  reporting: "analysis",
  integration: "management",
};

/**
 * Generate quick actions from the WORKFLOW_REGISTRY.
 * Each entry becomes a `kind: 'workflow-run'` action with `source: 'workflow-registry'`.
 */
export function getWorkflowActions(): QuickAction[] {
  return WORKFLOW_REGISTRY.map((def) => {
    const icon = ICON_MAP[def.iconKey] ?? HelpCircle;
    return {
      id: `workflow-run-${def.id}`,
      kind: "workflow-run" as const,
      label: def.name,
      description: def.description,
      icon,
      category: CATEGORY_MAP[def.category] ?? "productivity",
      prompt: `Run workflow: ${def.name}`,
      workflowId: def.id,
      keywords: [
        def.name.toLowerCase(),
        def.domain,
        def.category,
        "workflow",
        "run",
        ...def.steps.map((s) => s.label.toLowerCase()),
      ],
      priority: 6,
      domain: def.domain,
      source: "workflow-registry" as const,
      estimatedTime: undefined,
    };
  });
}
